import { motion, AnimatePresence } from 'framer-motion'
import { useState, useEffect } from 'react'
import { X, Cookie, Shield, CheckCircle } from 'lucide-react'

const CookieConsent = () => {
  const [isVisible, setIsVisible] = useState(false)
  const [preferences, setPreferences] = useState({
    necessary: true, // Always required
    analytics: false,
    marketing: false
  })
  const [showDetails, setShowDetails] = useState(false)

  useEffect(() => {
    const consent = localStorage.getItem('cookieConsent')
    if (!consent) {
      // Show banner after 2 seconds
      setTimeout(() => setIsVisible(true), 2000)
    }
  }, [])

  const acceptAll = () => {
    const allAccepted = {
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }
    localStorage.setItem('cookieConsent', JSON.stringify(allAccepted))
    setIsVisible(false)
    
    // Initialize analytics if accepted
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: 'granted',
        ad_storage: 'granted'
      })
    }
  }

  const acceptSelected = () => {
    const consent = {
      ...preferences,
      timestamp: new Date().toISOString()
    }
    localStorage.setItem('cookieConsent', JSON.stringify(consent))
    setIsVisible(false)

    // Update analytics consent
    if (window.gtag) {
      window.gtag('consent', 'update', {
        analytics_storage: preferences.analytics ? 'granted' : 'denied',
        ad_storage: preferences.marketing ? 'granted' : 'denied'
      })
    }
  }

  const rejectAll = () => {
    const minimalConsent = {
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }
    localStorage.setItem('cookieConsent', JSON.stringify(minimalConsent))
    setIsVisible(false)
  }

  if (!isVisible) return null

  return (
    <AnimatePresence>
      <motion.div
        initial={{ y: 100, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        exit={{ y: 100, opacity: 0 }}
        className="fixed bottom-0 left-0 right-0 z-[200] p-4 sm:p-6"
      >
        <div className="max-w-6xl mx-auto bg-gradient-to-br from-bronze-900 to-bronze-950 rounded-2xl shadow-2xl border-2 border-gold-400/30 overflow-hidden">
          <div className="p-6 sm:p-8">
            {/* Header */}
            <div className="flex items-start gap-4 mb-6">
              <div className="w-12 h-12 bg-gold-400/20 rounded-full flex items-center justify-center flex-shrink-0">
                <Cookie className="w-6 h-6 text-gold-400" />
              </div>
              <div className="flex-1">
                <h3 className="text-xl sm:text-2xl font-display font-bold text-cream mb-2">
                  🍪 Përdorim Cookies
                </h3>
                <p className="text-beige-300 text-sm sm:text-base">
                  Ne përdorim cookies për të përmirësuar përvojën tuaj, analizuar trafikun dhe personalizuar përmbajtjen. 
                  Zgjidhni preferencat tuaja më poshtë.
                </p>
              </div>
            </div>

            {/* Cookie Details */}
            {showDetails && (
              <motion.div
                initial={{ height: 0, opacity: 0 }}
                animate={{ height: 'auto', opacity: 1 }}
                exit={{ height: 0, opacity: 0 }}
                className="mb-6 space-y-4"
              >
                {/* Necessary Cookies */}
                <div className="flex items-start gap-3 p-4 bg-bronze-800/50 rounded-xl">
                  <input
                    type="checkbox"
                    checked={true}
                    disabled
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Shield className="w-4 h-4 text-blue-400" />
                      <span className="font-semibold text-cream">Cookies të Nevojshme</span>
                      <span className="text-xs px-2 py-0.5 bg-blue-500/20 text-blue-400 rounded">Të detyrueshme</span>
                    </div>
                    <p className="text-beige-400 text-sm">
                      Këto cookies janë thelbësore për funksionimin e website-it dhe nuk mund të çaktivizohen.
                    </p>
                  </div>
                </div>

                {/* Analytics Cookies */}
                <div className="flex items-start gap-3 p-4 bg-bronze-800/50 rounded-xl">
                  <input
                    type="checkbox"
                    checked={preferences.analytics}
                    onChange={(e) => setPreferences({ ...preferences, analytics: e.target.checked })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-400" />
                      <span className="font-semibold text-cream">Cookies Analitike</span>
                    </div>
                    <p className="text-beige-400 text-sm">
                      Na ndihmojnë të kuptojmë si përdoruesit ndërveprojnë me website-in përmes Google Analytics.
                    </p>
                  </div>
                </div>

                {/* Marketing Cookies */}
                <div className="flex items-start gap-3 p-4 bg-bronze-800/50 rounded-xl">
                  <input
                    type="checkbox"
                    checked={preferences.marketing}
                    onChange={(e) => setPreferences({ ...preferences, marketing: e.target.checked })}
                    className="mt-1"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-1">
                      <Cookie className="w-4 h-4 text-orange-400" />
                      <span className="font-semibold text-cream">Cookies Marketing</span>
                    </div>
                    <p className="text-beige-400 text-sm">
                      Përdoren për të shfaqur reklama të personalizuara bazuar në interesat tuaja.
                    </p>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="px-6 py-3 bg-bronze-800 hover:bg-bronze-700 text-cream rounded-xl transition-colors text-sm font-medium"
              >
                {showDetails ? 'Fshih Detajet' : 'Shfaq Detajet'}
              </button>
              
              <div className="flex-1 flex flex-col sm:flex-row gap-3">
                <button
                  onClick={rejectAll}
                  className="flex-1 px-6 py-3 bg-bronze-800 hover:bg-bronze-700 text-cream rounded-xl transition-colors text-sm font-medium"
                >
                  Refuzo Të Gjitha
                </button>
                
                {showDetails && (
                  <button
                    onClick={acceptSelected}
                    className="flex-1 px-6 py-3 bg-gold-600 hover:bg-gold-500 text-white rounded-xl transition-colors text-sm font-medium"
                  >
                    Ruaj Preferencat
                  </button>
                )}
                
                <button
                  onClick={acceptAll}
                  className="flex-1 px-6 py-3 bg-gradient-gold text-white rounded-xl shadow-lg hover:shadow-gold-400/50 transition-all text-sm font-bold"
                >
                  Prano Të Gjitha
                </button>
              </div>
            </div>

            {/* Privacy Policy Link */}
            <div className="mt-4 text-center">
              <a
                href="/privacy-policy"
                className="text-xs text-beige-400 hover:text-gold-400 transition-colors underline"
              >
                Lexo Politikën e Privatësisë
              </a>
            </div>
          </div>
        </div>
      </motion.div>
    </AnimatePresence>
  )
}

export default CookieConsent
